<template>
  <v-flex fill fill-height horizontal>
    <div class="text-xs-center" v-if="loading">
      <v-progress-circular
        :size="50"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </div>
    <div v-else class="header-section">
      <h1>{{ season.name }} Points - {{ season.weeks_for_points }} weeks</h1>
      <p>
        The highest <em>Points - {{ season.weeks_for_points }} weeks</em> in
        each division receives a trophy at the end of the season.
      </p>
      <week-list :season="season"></week-list>
    </div>

    <section
      class="elevation-1"
      v-for="division in divisions"
      v-bind:key="division[0].player.division"
    >
      <div
        v-sticky="stickyConfig"
        style="background:white"
        class="table-header-section"
      >
        <h3 class="pa-3">{{ division[0].player.division }} Season Standings</h3>
        <v-data-table
          ref="headerTable"
          :headers="headers"
          :items="[]"
          hide-actions
          disable-initial-sort
          dense
          v-bind:id="`header-table-${division[0].player.division}`"
          v-scrollsync="`main-table-${division[0].player.division}`"
        ></v-data-table>
      </div>

      <v-data-table
        :headers="headers"
        :items="division"
        hide-actions
        ref="dataTable"
        class="division-table"
        dense
        v-bind:id="`main-table-${division[0].player.division}`"
        v-scrollsync="`header-table-${division[0].player.division}`"
      >
        <template slot="items" slot-scope="props">
          <td class="rollup-cell-sm">{{ props.item.place }}</td>
          <td class="w-50">
            <router-link
              :to="{
                name: 'playerStats',
                params: playerRouteParams(props.item.player),
              }"
              >{{ props.item.player.name | shortName }}</router-link
            >
          </td>
          <td class="rollup-cell-md">{{ props.item.points }}</td>
          <td class="rollup-cell-md">{{ props.item.weeks }}</td>
          <td class="rollup-cell-md hidden-sm-and-down">
            {{ props.item.strokes }}
          </td>
          <td class="rollup-cell-sm hidden-sm-and-down">
            {{ props.item.from_par }}
          </td>
          <td class="rollup-cell-sm hidden-xs-only">{{ props.item.wins }}</td>
          <td class="rollup-cell-md hidden-xs-only">{{ props.item.cashes }}</td>
        </template>
      </v-data-table>
    </section>
  </v-flex>
</template>

<script>
import VueSticky from "vue-sticky";
import WeekList from "./shared/WeekSelector";

export default {
  name: "SeasonStandings",
  props: ["seasonId", "seasonName"],
  components: {
    WeekList,
  },
  directives: {
    sticky: VueSticky,
  },
  data() {
    return {
      loading: false,
      divisions: null,
      weeks: null,
      error: null,
      season: null,
      stickyConfig: {
        zIndex: 1,
        stickyTop: 64,
        disabled: false,
      },
      headers: [
        {
          text: "Place",
          value: "top_ten_place",
          align: "left",
          width: 25,
          sortable: false,
        },
        { text: "Name", value: "player.name", align: "left", sortable: false },
        {
          text: "Points",
          value: "top_ten_par_avg",
          align: "left",
          width: 100,
          sortable: false,
        },
        {
          text: "Weeks",
          value: "season_par_avg",
          align: "left",
          width: 75,
          sortable: false,
        },
        {
          text: "Strokes",
          value: "season_strokes",
          align: "left",
          width: 100,
          sortable: false,
          class: "hidden-sm-and-down",
        },
        {
          text: "Par",
          value: "season_par",
          align: "left",
          width: 100,
          sortable: false,
          class: "hidden-sm-and-down",
        },
        {
          text: "Wins",
          value: "season_wins",
          align: "left",
          width: 75,
          sortable: false,
          class: "hidden-xs-only",
        },
        {
          text: "Cashes",
          value: "season_cashed",
          align: "left",
          width: 75,
          sortable: false,
          class: "hidden-xs-only",
        },
      ],
    };
  },
  methods: {
    async fetchData() {
      this.error = this.divisions = null;
      this.loading = true;
      try {
        const results = await Promise.all([
          this.$apiClient.seasons(this.seasonId),
          this.$apiClient.points(this.seasonId),
        ]);

        this.season = results[0].data;

        // reduce results into divisions for display.
        const sorted = results[1].data.reduce(function(result, entry) {
          const bracket = entry.player.division.toLowerCase();
          result[bracket] = result[bracket] || [];
          result[bracket].push(entry);
          return result;
        }, {});

        const divisionsWithPlayers = [];
        if ((sorted.pro || []).length > 0)
          divisionsWithPlayers.push(sorted.pro);
        if ((sorted.adv || []).length > 0)
          divisionsWithPlayers.push(sorted.adv);
        if ((sorted.rec || []).length > 0)
          divisionsWithPlayers.push(sorted.rec);

        this.divisions = divisionsWithPlayers;
      } catch (e) {
        this.error = e;
      }

      this.loading = false;
    },
    adjustOffset() {
      this.stickyConfig.stickyTop =
        ["xs", "sm"].indexOf(this.$vuetify.breakpoint.name) > -1 ? 48 : 64;
    },
    handleScroll() {
      return true;
    },
    getWeekRouteParams(weekId) {
      if (!this.season) {
        return {};
      }
      return {
        season: this.season.season_id,
        season_name: this.season.name.replace(" ", "-"),
        week: weekId,
      };
    },
    playerRouteParams(player) {
      return {
        seasonId: this.season.season_id,
        seasonName: this.season.name.replace(/\s/g, "-"),
        playerId: player.player_id,
        playerName: player.name.replace(/\s/g, "-"),
      };
    },
  },
  watch: {
    // call again the method if the route changes
    $route: "fetchData",
    "$vuetify.breakpoint": "adjustOffset",
  },
  created() {
    this.fetchData();
    this.adjustOffset();
  },
};
</script>

<style>
section table th {
  text-align: left;
}
/*.table-header-section {*/
/*    z-index: auto !important;*/
/*}*/
.table-header-section table.v-table tbody td {
  display: none !important;
  height: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
}

.division-table table.v-table thead tr:last-child {
  display: none !important;
}

.division-table table.v-table {
  margin-top: -20px;
}

.division-table table.v-table thead {
  visibility: hidden !important;
}
.division-table table.v-table thead tr {
  height: 0 !important;
}
.division-table table.v-table thead th {
  height: 0 !important;
  padding-top: 0;
  padding-bottom: 0;
}
.header-section {
  padding-bottom: 15px;
}
</style>
